import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { VonMessageService } from '@von-development-studio/primeng-message-service';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { BaseService } from 'src/app/core/base/base.service';
import { RestService } from 'src/app/core/service/rest.service';
import { AdminCatalogModel } from 'src/app/model/admin/catalog';
import { BaseModalModel } from 'src/app/model/core/base-modal';
import { adminCatalogActions } from 'src/app/store/admin/catalog/admin-catalog.action';
import { environment } from 'src/environments/environment';
import { SUBCATALOG_CONSTANTS } from './admin-subcatalog.constants';

@Injectable({
  providedIn: 'root',
})
export class AdminSubCatalogService extends BaseService {
  private readonly screen = SUBCATALOG_CONSTANTS;

  constructor(
    protected store: Store<any>,
    protected rest: RestService,
    protected message: VonMessageService
  ) {
    super();
  }

  getSubCatalogStore = (key: string): Observable<AdminCatalogModel[]> =>
    this.store.select('admin', 'catalog', 'subCatalogs', key);

  getSubCatalogModal = (): Observable<BaseModalModel<AdminCatalogModel>> =>
    this.store.select('admin', 'catalog', 'modal');

  openModal = ($event?: AdminCatalogModel) => {
    const modalInfo = {} as BaseModalModel<AdminCatalogModel>;
    modalInfo.isVisible = true;
    if (!$event) {
      modalInfo.title = this.screen.actions.add;
      modalInfo.value = {} as AdminCatalogModel;
    } else {
      modalInfo.title = this.screen.actions.edit;
      modalInfo.value = { ...$event };
    }
    this.store.dispatch(adminCatalogActions.setSubCatalogAction(modalInfo));
  };

  readSubCatalogs = (
    code: string,
    onlyActive = false
  ): Observable<AdminCatalogModel[]> =>
    this.rest
      .get<AdminCatalogModel[]>({
        url: `${environment.endpoints.admin}/catalog/parent/{code}`,
        urlParams: { code },
        params: { onlyActive },
      })
      .pipe(
        tap((value) =>
          this.store.dispatch(
            adminCatalogActions.addSubCatalogAction({ code, value })
          )
        )
      );

  save = (
    code: string,
    $event?: AdminCatalogModel
  ): Observable<AdminCatalogModel> => {
    const operation =
      $event && $event.id
        ? this.rest.patch<AdminCatalogModel>({
            url: `${environment.endpoints.admin}/catalog`,
            body: $event,
          })
        : this.rest.put<AdminCatalogModel>({
            url: `${environment.endpoints.admin}/catalog`,
            body: {
              ...$event,
              parent: code,
            },
          });

    return operation.pipe(
      tap((catalog) => {
        this.message.addSuccess(this.screen.labels.saveSuccess);
        this.store.dispatch(
          adminCatalogActions.setSubCatalogDefaultAction(true)
        );
        if ($event && $event.id) {
          this.store.dispatch(
            adminCatalogActions.patchSubCatalogAction({ code, value: catalog })
          );
        } else if (catalog.id) {
          this.store.dispatch(
            adminCatalogActions.putSubCatalogAction({ code, value: catalog })
          );
        }
      })
    );
  };

  patchStatus = (
    parentCode: string,
    code?: string
  ): Observable<AdminCatalogModel> =>
    this.rest
      .patch<AdminCatalogModel>({
        url: `${environment.endpoints.admin}/catalog/status/{code}`,
        urlParams: { code },
      })
      .pipe(
        tap((catalog) => {
          this.message.addSuccess(this.screen.labels.saveSuccess);
          this.store.dispatch(
            adminCatalogActions.patchSubCatalogAction({
              code: parentCode,
              value: {
                ...catalog,
                status: !catalog.status,
              },
            })
          );
        })
      );
}
